import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { searchContent, type SearchResult } from "@/utils/content";

interface SiteSearchProps {
  open: boolean;
  onClose: () => void;
}

const typeStyle: Record<SearchResult["type"], string> = {
  剧情: "bg-primary-100 text-primary-700",
  角色: "bg-accent-100 text-accent-800",
  资讯: "bg-secondary-200 text-secondary-800",
};

export default function SiteSearch({ open, onClose }: SiteSearchProps) {
  const [keyword, setKeyword] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setKeyword("");
      setResults([]);
      const t = window.setTimeout(() => inputRef.current?.focus(), 80);
      return () => window.clearTimeout(t);
    }
    return undefined;
  }, [open]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    if (open) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  useEffect(() => {
    setResults(searchContent(keyword));
  }, [keyword]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center px-4 pt-20 md:pt-28">
      <div
        className="absolute inset-0 bg-foreground-950/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />
      <div className="relative w-full max-w-2xl rounded-lg bg-background-50 border border-background-200 overflow-hidden">
        <div className="flex items-center gap-3 px-4 py-3 border-b border-background-200">
          <div className="w-5 h-5 flex items-center justify-center text-foreground-500">
            <i className="ri-search-line text-lg" />
          </div>
          <input
            ref={inputRef}
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="搜索剧集、角色或资讯标题…"
            className="flex-1 bg-transparent outline-none text-sm text-foreground-900 placeholder:text-foreground-400"
          />
          <button
            type="button"
            onClick={onClose}
            className="w-7 h-7 flex items-center justify-center rounded-md text-foreground-500 hover:bg-background-200 cursor-pointer"
            aria-label="关闭搜索"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <div className="max-h-[60vh] overflow-y-auto jnz-scrollbar">
          {keyword.trim() === "" ? (
            <div className="px-5 py-8 text-center text-sm text-foreground-500">
              输入关键词，快速检索整站内容
            </div>
          ) : results.length === 0 ? (
            <div className="px-5 py-8 text-center text-sm text-foreground-500">
              未找到与「{keyword}」相关的内容
            </div>
          ) : (
            <ul className="py-2">
              {results.map((r) => (
                <li key={`${r.type}-${r.id}`}>
                  <Link
                    to={r.link}
                    onClick={onClose}
                    className="flex items-start gap-3 px-5 py-3 hover:bg-background-100 cursor-pointer"
                  >
                    <span
                      className={`mt-0.5 shrink-0 rounded-full px-2 py-0.5 text-xs ${typeStyle[r.type]}`}
                    >
                      {r.type}
                    </span>
                    <span className="min-w-0">
                      <span className="block text-sm font-medium text-foreground-900 line-clamp-1">
                        {r.title}
                      </span>
                      <span className="mt-0.5 block text-xs text-foreground-500 line-clamp-1">
                        {r.desc}
                      </span>
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}